import React from 'react';
import { Box, Typography } from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import type { MulticamAnnotation } from '../../graphqlService';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import { formatTimecode } from '../../utils/timecode';
import type { ScrubMarker } from '../AnnotationStudio/TransportBar';
import type { StageNote } from '../AnnotationStudio/VideoStage';
import type { TimelineItem, TimelineLane } from '../Timeline/timelineTypes';
import {
  angleName,
  cutLabel,
  findAngle,
  isCut,
  programSegments,
  sortedCuts,
  toPanelAnnotation,
} from './multicamStudioModel';
import { angleColor, playerTokens } from './multicamTheme';

/**
 * What the multicam studio draws from its annotations: the timeline's lanes,
 * the ticks on the transport's scrub bar, and the notes laid over the picture.
 */

type NamedAngle = { angleKey: string; label?: string | null };

const rangeEnd = (annotation: MulticamAnnotation) =>
  typeof annotation.endTimeMs === 'number' && annotation.endTimeMs > annotation.timelineTimeMs
    ? annotation.endTimeMs / 1000
    : null;

interface LaneOptions {
  annotations: MulticamAnnotation[];
  angles: NamedAngle[];
  masterKey: string;
  /** Length of the shared timeline in seconds. */
  duration: number;
  editable: boolean;
  hasFootage: (angleKey: string, seconds: number) => boolean;
}

/**
 * Three lanes: Angles (which angle the cuts show over the program), Cuts
 * (the cut points, dragged to retime) and Notes (as in the studio).
 */
export const buildMulticamLanes = ({ annotations, angles, masterKey, duration, editable, hasFootage }: LaneOptions): TimelineLane[] => {
  const segments = programSegments(annotations, masterKey, duration);
  const cuts = sortedCuts(annotations);
  const notes = annotations.filter((annotation) => !isCut(annotation));
  return [
    {
      id: 'angles',
      label: 'Angles',
      icon: <VideocamOutlinedIcon />,
      kind: 'tasks',
      maxRows: 1,
      editable: false,
      items: segments.map((segment) => {
        const angle = findAngle(angles, segment.angleKey);
        const name = angle ? angleName(angle) : `Angle ${segment.angleKey}`;
        const recorded = hasFootage(segment.angleKey, segment.start + 1e-3);
        return {
          id: segment.id,
          start: segment.start,
          end: segment.end,
          label: name,
          color: angleColor(segment.angleKey),
          editable: false,
          dimmed: !recorded,
          ariaLabel: segment.cutId ? name : `${name}, before the first cut`,
          tooltip: recorded ? undefined : (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.45 }}>
              {name} has no footage at the start of this stretch.
            </Typography>
          ),
        };
      }),
    },
    {
      id: 'cuts',
      label: 'Cuts',
      icon: <ContentCutOutlinedIcon />,
      kind: 'markers',
      editable,
      maxRows: 2,
      emptyText: 'Camera cuts you add appear here. Drag one to retime it.',
      items: cuts.map((cut) => {
        const label = cutLabel(cut.targetAngleKey, angles);
        const reason = cut.text.trim();
        return {
          id: cut.id,
          start: cut.timelineTimeMs / 1000,
          end: null,
          label,
          color: angleColor(cut.targetAngleKey),
          ariaLabel: reason ? `${label}: ${reason}` : label,
          tooltip: (
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}>{label}</Typography>
              {reason && (
                <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: playerTokens.textMuted }}>{reason}</Typography>
              )}
            </Box>
          ),
        };
      }),
    },
    {
      id: 'notes',
      label: 'Notes',
      icon: <ChatBubbleOutlineOutlinedIcon />,
      kind: 'markers',
      editable,
      maxRows: 3,
      emptyText: 'Notes you add appear here. Drag a marker to retime it.',
      items: notes.map((note) => {
        const category = getAnnotationCategory(note.category, note.color);
        return {
          id: note.id,
          start: note.timelineTimeMs / 1000,
          end: rangeEnd(note),
          label: note.text,
          color: category.color,
          ariaLabel: `Note (${category.label}): ${note.text}`,
          tooltip: (
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: playerTokens.textMuted }}>{category.label}</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.45 }}>{note.text}</Typography>
            </Box>
          ),
        };
      }),
    },
  ];
};

/** Timeline items: an angles-lane stretch stands for the cut that starts it (none for the master). */
export const annotationIdOf = (item: TimelineItem): string | null => {
  if (!item.id.startsWith('segment:')) return item.id;
  const cutId = item.id.slice('segment:'.length);
  return cutId === 'start' ? null : cutId;
};

/** Ticks on the dark scrub bar: cuts in white, notes in their category colour. */
export const buildScrubMarkers = (annotations: MulticamAnnotation[]): ScrubMarker[] =>
  annotations.map((annotation) => {
    const cut = isCut(annotation);
    return {
      time: annotation.timelineTimeMs / 1000,
      endTime: cut ? null : rangeEnd(annotation),
      color: cut ? playerTokens.text : getAnnotationCategory(annotation.category, annotation.color).color,
    };
  });

/** The cuts and notes that apply at the playhead, laid over the picture. */
export const buildStageNotes = (
  annotations: MulticamAnnotation[],
  activeIds: string[],
  angles: NamedAngle[],
  fps: number
): StageNote[] => {
  const ids = new Set(activeIds);
  return annotations
    .filter((annotation) => ids.has(annotation.id))
    .map((annotation) => {
      const timecode = formatTimecode(annotation.timelineTimeMs / 1000, fps);
      if (isCut(annotation)) {
        return {
          id: annotation.id,
          label: `Camera cut · ${timecode}`,
          text: toPanelAnnotation(annotation, angles).text,
          color: angleColor(annotation.targetAngleKey),
        };
      }
      const category = getAnnotationCategory(annotation.category, annotation.color);
      return { id: annotation.id, label: `${category.label} · ${timecode}`, text: annotation.text, color: category.color };
    });
};
